import { ReactNode } from 'react'
import { Lock } from 'lucide-react'
import { FOLDER_GRADIENTS } from '../../lib/folderColors'

/** Tilts an element toward the pointer (mouse only). */
export function tiltHandlers() {
  return {
    onPointerMove: (e: React.PointerEvent<HTMLElement>) => {
      if (e.pointerType !== 'mouse') return
      const el = e.currentTarget
      const r = el.getBoundingClientRect()
      const x = (e.clientX - r.left) / r.width
      const y = (e.clientY - r.top) / r.height
      el.style.setProperty('--ry', `${((x - 0.5) * 12).toFixed(2)}deg`)
      el.style.setProperty('--rx', `${((0.5 - y) * 12).toFixed(2)}deg`)
      el.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`)
      el.style.setProperty('--my', `${(y * 100).toFixed(1)}%`)
    },
    onPointerLeave: (e: React.PointerEvent<HTMLElement>) => {
      e.currentTarget.style.setProperty('--rx', '0deg')
      e.currentTarget.style.setProperty('--ry', '0deg')
    },
  }
}

interface KeyCardStackProps {
  /** Spread the fanned cards (e.g. while unlocking). */
  spread?: boolean
  /** Hide the fanned cards behind the key card. */
  bare?: boolean
  children: ReactNode
  className?: string
}

/**
 * A frosted "key card" with gradient cards fanned behind it. The cards
 * show through the glass, blurred.
 */
export function KeyCardStack({
  spread,
  bare,
  children,
  className = '',
}: KeyCardStackProps) {
  return (
    // Padding (not margin) reserves room for the fanned cards so it never
    // collapses into the margin of the element above.
    <div className={`${bare ? '' : 'pt-[112px]'} ${className}`}>
      <div
        className="relative h-[208px] sm:h-[224px]"
        style={{ perspective: 1100 }}
      >
        {!bare &&
          [3, 2, 1, 0].map((i) => {
            const [c1, c2] = FOLDER_GRADIENTS[i]
            const y = spread ? -(i + 1) * 40 - 8 : -(i + 1) * 28
            const s = spread ? 1 - (i + 1) * 0.035 : 1 - (i + 1) * 0.045
            return (
              <div
                key={i}
                aria-hidden="true"
                className="gcard absolute inset-0 rounded-[28px]"
                style={{
                  ['--c1' as string]: c1,
                  ['--c2' as string]: c2,
                  transformOrigin: 'top center',
                  transform: `translateY(${y}px) scale(${s})`,
                  transition: 'transform .9s var(--soft)',
                  animation: `fanIn .9s var(--spring) ${i * 80}ms backwards`,
                }}
              />
            )
          })}
        {children}
      </div>
    </div>
  )
}

interface KeyCardProps {
  vaultId: string
  footerLeft: ReactNode
  footerRight?: ReactNode
  corner?: ReactNode
  dim?: boolean
}

export function KeyCard({
  vaultId,
  footerLeft,
  footerRight,
  corner,
  dim,
}: KeyCardProps) {
  return (
    <div
      {...tiltHandlers()}
      className="keycard absolute inset-0 flex flex-col justify-between overflow-hidden rounded-[28px] p-5 sm:p-6"
      style={{
        transform: 'rotateX(var(--rx,0deg)) rotateY(var(--ry,0deg))',
        transition: 'transform .6s var(--ease)',
        animation: 'cardIn 1s var(--spring) .2s backwards',
      }}
    >
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2.5">
          <span className="mark h-[34px] w-[34px] rounded-[11px]">
            <Lock className="h-[17px] w-[17px]" strokeWidth={2} />
          </span>
          <span className="font-display text-[19px] font-semibold tracking-tight">
            Vault
          </span>
        </span>
        {corner}
      </div>
      <div
        className={`font-mono text-[18px] tracking-[0.14em] sm:text-[21px] ${dim ? 'text-white/40' : 'text-white'}`}
        aria-label={`Vault ID ${vaultId.split('').join(' ')}`}
      >
        {vaultId}
      </div>
      <div className="flex items-center justify-between gap-3 text-[13px] text-[#C9C7D8]">
        <span className="min-w-0 truncate">{footerLeft}</span>
        {footerRight}
      </div>
    </div>
  )
}
